import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import i18n from 'i18n-js';

import { scale, notchTop } from '@components/frame';
import { isPhone, SourceSystems, Alert, ERP_SYSTEM_CONNECTION_TYPE, insertPathBreakOpportunities } from '@helpers';

import EditableLine from '@components/common/editableLine';
import Line from '@components/common/line';
import TouchableElement from '@components/common/touchableElement';
import { SmallLoading } from '@components/loading';

import { font } from '@styles/fonts';

import { ConnectorDetailsProps, InputLine } from '@components/settings/v2/structure/connector/productionConnectorSetup/interfaces';
import { modalFieldBg, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import ValidationErrorsPopup from './ValidationErrorsPopup';
import { SourceSystem } from '@interfaces/connector';

const ProductionConnectorSetup: React.FC<ConnectorDetailsProps> = ({
  C,
  setMode,
  MODES,
  updateDataHistory,
  updateDataHistoryObj,
  setData,
  setDataHistory,
  goBack,
  data,
  onboardProductionSourceSystem,
  maxHeight,
  hide,
  connectorAlreadyExist,
  connectorProductionConfigurationDetails,
  isEditMode,
  setIsEditMode,
  dataHistory,
  isDataChanged,
  setIsDataChanged,
  isError,
}) => {
  const [loading, setLoading] = useState(false);
  const [sendingToProMExS, setSendingToProMExS] = useState<boolean>(false);
  const [connectionInProgress, setConnectionInProgress] = useState<boolean>(false);

  const { folders = [], selectedType } = dataHistory;

  const placeholder = (n: string, value: any) => (value ? '*****' : n);

  const hints = {} as any;

  const isMKG = dataHistory?.selectedType?.emptySourceSystemConfiguration?.code === SourceSystems.MKG;
  const isMKG_API = dataHistory?.selectedType?.sourceSystemConnectionType === ERP_SYSTEM_CONNECTION_TYPE.MKG_API && isMKG;
  const isMKG_ODBC = dataHistory?.selectedType?.sourceSystemConnectionType === ERP_SYSTEM_CONNECTION_TYPE.MKG_ODBC && isMKG;

  const shouldDisplayAPIKey = isMKG && isMKG_API;

  selectedType?.sourceSystemConnectionFields.forEach((i) => {
    const key = i.name.charAt(0).toLowerCase() + i.name.slice(1);
    hints[key] = i.hint;
  });

  const inputsConnectionTitle = isMKG_API ? 'URL' : '';
  const inputsConnectionDescription = isMKG_API
    ? 'The URL should include both the port number and the root path. For most users, the root path is /mkg when using the cloud version and /mkgbridge for on-premise deployments.'
    : '';

  let inputsConnection = [] as InputLine[];

  (() => {
    switch (selectedType?.code) {
      case SourceSystems.MKG: {
        if (isMKG_API) {
          inputsConnection = [
            {
              key: 'apiRootEndpoint',
              name: '',
              placeholder: hints['apiRootEndpoint'] || `${i18n.t('E.q.')} https://domein.com:8443/mkgbridge`,
              noArrow: true,
              isLable: false,
              placeholderAlreadyTranslated: !hints['apiRootEndpoint'],
            },
          ];
          break;
        }

        if (isMKG_ODBC) {
          inputsConnection = [
            {
              key: 'dataSourceName',
              name: 'DSN',
              placeholder: hints['dataSourceName'] || 'Data Source Name',
              noArrow: true,
              isLable: true,
            },
            {
              key: 'host',
              name: 'Host',
              placeholder: hints['host'] || 'Host',
              noArrow: true,
              isLable: true,
            },
            {
              key: 'port',
              name: 'Port',
              placeholder: hints['port'] || 'Port',
              noArrow: true,
              isLable: true,
            },
            {
              key: 'database',
              name: 'Database',
              placeholder: hints['database'] || 'Database',
              noArrow: true,
              isLable: true,
            },
          ];
          break;
        }
      }

      case SourceSystems.Bemet: {
        inputsConnection = [
          {
            key: 'host',
            name: 'Host',
            placeholder: hints['host'] || 'Host',
            noArrow: true,
            isLable: true,
          },
          {
            key: 'port',
            name: 'Port',
            placeholder: hints['port'] || 'Port',
            noArrow: true,
            isLable: true,
          },
          {
            key: 'database',
            name: 'Database',
            placeholder: hints['database'] || 'Database',
            noArrow: true,
            isLable: true,
          },
        ];
        break;
      }
      case SourceSystems.proMExS: {
        inputsConnection = [
          {
            key: 'serverEndpoint',
            name: 'Endpoint',
            placeholder: hints['serverEndpoint'] || 'Server Endpoint',
            noArrow: true,
            isLable: true,
          },
          {
            key: 'databaseName',
            name: 'Database',
            placeholder: hints['databaseName'] || 'Database Name',
            noArrow: true,
            isLable: true,
          },
        ];
        break;
      }
    }
  })();

  const inputsDatabase = [
    {
      key: 'user',
      name: 'User',
      placeholder: hints['user'] || 'User',
      noArrow: true,
      isLable: true,
    },
    {
      key: 'password',
      name: 'Password',
      placeholder: placeholder('Password', connectorAlreadyExist),
      noArrow: true,
      isLable: true,
      secure: true,
    },
  ];

  const inputsFile = [
    {
      key: 'fileUser',
      name: 'User',
      placeholder: hints['fileUser'] || 'User',
      noArrow: true,
      isLable: true,
    },
    {
      key: 'filePassword',
      name: 'Password',
      placeholder: placeholder('Password', connectorAlreadyExist),
      noArrow: true,
      isLable: true,
      secure: true,
    },
    {
      key: 'fileDomain',
      name: 'Domain',
      placeholder: hints['fileDomain'] || 'Domain or Workgroup',
      noArrow: true,
      isLable: true,
    },
  ];

  if (selectedType?.hasDocumentsRootFolder) {
    inputsFile.push({
      key: 'documentsRootFolder',
      name: 'Root Folder',
      placeholder: 'Documents Root Folder',
      noArrow: true,
      isLable: true,
    });
  }

  const submit = async () => {
    setLoading(true);

    const base = JSON.parse(JSON.stringify(dataHistory?.selectedType?.emptySourceSystemConfiguration));

    base.timeZoneIana = '';
    base.isEnabled = dataHistory.isEnabled ?? true;
    base.timeZoneWindows = dataHistory.timeZoneWindows;

    if (dataHistory.fileDomain !== data.fileDomain || dataHistory.fileUser !== data.fileUser || dataHistory.filePassword) {
      base.documentsConnectionProperties.credentials.userName = `${dataHistory.fileDomain}\\${dataHistory.fileUser}`;
      base.documentsConnectionProperties.credentials.password = dataHistory.filePassword;
    } else {
      base.documentsConnectionProperties.credentials = null;
    }

    base.documentsConnectionProperties.documentSources = folders.map((i) => ({
      credentials: null,
      mappedNetworkDrive: i.folder,
      networkPath: i.UNC,
    }));

    const mapper = {
      [`${SourceSystems.MKG} ${ERP_SYSTEM_CONNECTION_TYPE.MKG_ODBC}`]: 'databaseOdbcConnectionProperties',
      [`${SourceSystems.MKG} ${ERP_SYSTEM_CONNECTION_TYPE.MKG_API}`]: 'apiConnectionProperties',
      [`${SourceSystems.Bemet} ${ERP_SYSTEM_CONNECTION_TYPE.Bemet}`]: 'databaseNpgsqlConnectionProperties',
      [`${SourceSystems.proMExS} ${ERP_SYSTEM_CONNECTION_TYPE.proMExS}`]: 'databaseSqlClientConnectionProperties',
    };

    const dynamicKey = `${base.code} ${base.sourceSystemConnectionType}`;

    if (dataHistory.user !== data.user || dataHistory.password) {
      base[mapper[dynamicKey]].credentials.userName = dataHistory.user;
      base[mapper[dynamicKey]].credentials.password = dataHistory.password;
    } else {
      base[mapper[dynamicKey]].credentials = null;
    }

    if (dataHistory?.selectedType?.hasDocumentsRootFolder) {
      base.documentsConnectionProperties.documentsRootFolder = dataHistory.documentsRootFolder;
    }

    switch (base.code) {
      case SourceSystems.MKG: {
        if (base.sourceSystemConnectionType === ERP_SYSTEM_CONNECTION_TYPE.MKG_ODBC) {
          base.databaseOdbcConnectionProperties.host = dataHistory.host;
          base.databaseOdbcConnectionProperties.dataSourceName = dataHistory.dataSourceName;
          if (dataHistory.database) {
            base.databaseOdbcConnectionProperties.database = dataHistory.database;
          }
          if (dataHistory.port) {
            base.databaseOdbcConnectionProperties.port = dataHistory.port;
          }
        }

        if (base.sourceSystemConnectionType === ERP_SYSTEM_CONNECTION_TYPE.MKG_API) {
          base.apiConnectionProperties.apiKey = dataHistory?.apiKey;
          base.apiConnectionProperties.apiRootEndpoint = dataHistory?.apiRootEndpoint;
        }

        break;
      }
      case SourceSystems.Bemet: {
        base.databaseNpgsqlConnectionProperties.host = dataHistory.host;
        if (dataHistory.port) {
          base.databaseNpgsqlConnectionProperties.port = dataHistory.port;
        }
        if (dataHistory.database) {
          base.databaseNpgsqlConnectionProperties.database = dataHistory.database;
        }
        break;
      }
      case SourceSystems.proMExS: {
        base.databaseSqlClientConnectionProperties.serverEndpoint = dataHistory.serverEndpoint;
        if (dataHistory.databaseName) {
          base.databaseSqlClientConnectionProperties.databaseName = dataHistory.databaseName;
        }
        break;
      }
    }

    selectedType?.requiredFilterProperties?.forEach((i) => {
      base.filterProperties[i.code] = dataHistory[i.code];
    });

    const onSuccess = () => {
      Alert({
        title: 'Updating Configuration',
        text: 'This may take up to one minute to complete. You’ll receive a confirmation once the process is finished.',
        loadingIndicator: true,
        actions: [
          {
            text: i18n.t('Close'),
            onPress: () => {},
            style: 'primary',
          },
        ],
      } as any);
    };

    const onError = () => {
      setLoading(false);
      Alert({ type: 'danger', title: 'Updating Production Connector Configuration Failed' } as any);
    };

    const result = await onboardProductionSourceSystem({ sourceSystem: base }, onSuccess, onError);

    if (result == 'timeout') {
      setLoading(false);
      return Alert({
        title: 'Configuration Update Failed',
        titleColor: 'Label/Light/Error',
        text: 'Timeout error',
        actions: [
          {
            text: i18n.t('Close'),
            onPress: () => {},
            style: 'primary',
          },
        ],
      } as any);
    }

    if (result.validationResults) {
      setLoading(false);
      Alert({
        title: 'Updating Production Connector Configuration Failed',
        titleColor: 'Label/Light/Error',
        text: result.validationResults?.map((i: any) => i.message).join('\n\n') || 'Error',
        actions: [
          {
            text: i18n.t('Close'),
            onPress: () => {},
            style: 'primary',
          },
        ],
      } as any);
      return;
    } else {
      // Success - clear the saved data
      connectorProductionConfigurationDetails();
      setLoading(false);
      const cleanedData = { ...dataHistory, password: '', filePassword: '' };
      setData(JSON.parse(JSON.stringify(cleanedData)));
      setDataHistory(JSON.parse(JSON.stringify(cleanedData)));
      hide();

      setTimeout(() => {
        if (result.validationWarnings?.length) {
          setLoading(false);
          Alert({
            title: i18n.t('{{connectorDetailsCode}} Connection enabled with warnings', { connectorDetailsCode: selectedType?.code }),
            text: () => (
              <ValidationErrorsPopup
                connectorDetailsCode={selectedType?.code as SourceSystem}
                validationWarnings={result.validationWarnings}
                containerStyle={{ marginTop: scale(4) }}
              />
            ),
            actions: [
              {
                text: i18n.t('OK'),
                onPress: () => {},
                style: 'primary',
              },
            ],
          });
          return;
        }

        Alert({
          title: 'Updating Production Connector Successful Implementation In Progress',
          text: 'Your configuration has been successfully updated. You can close this message box, and the implementation process will continue in the background.',
          actions: [
            {
              text: i18n.t('Close'),
              onPress: () => {
                setConnectionInProgress(true);
              },
              style: 'primary',
            },
          ],
        } as any);
      }, 1000);
    }
  };

  const loaded = Boolean(selectedType) || isError;

  const shouldDisplayAPICredentials =
    dataHistory?.selectedType?.emptySourceSystemConfiguration?.code === SourceSystems.proMExS && connectorAlreadyExist && !isEditMode;

  const onSendingAPICredentials = async () => {
    if (sendingToProMExS) return;
    setSendingToProMExS(true);
    await onboardProductionSourceSystem(
      {
        sourceSystem: dataHistory?.selectedType?.emptySourceSystemConfiguration,
        doRefreshApiCredentials: true,
      },
      () => {},
      () => {}
    );
    setSendingToProMExS(false);
  };

  return (
    <View style={isPhone && { flex: 1 }}>
      <View style={{ paddingHorizontal: scale(15), zIndex: 1000 }}>
        <View
          style={{
            width: '100%',
            zIndex: 1000,
            height: scale(58),
          }}
        >
          {!isError && loaded && (
            <View style={{ position: 'absolute', zIndex: 1000, left: 0 }}>
              <TouchableElement
                onPress={() => {
                  if (connectorAlreadyExist) {
                    if (isEditMode) {
                      setIsEditMode(false);
                      setDataHistory(JSON.parse(JSON.stringify(data)));
                    } else {
                      setIsDataChanged(false);
                      setIsEditMode(true);
                    }
                  } else {
                    goBack();
                  }
                }}
              >
                {({ boxStyle, pressHoverColor }) => (
                  <View style={[boxStyle, { padding: scale(15), paddingLeft: 0, flexDirection: 'row' }]}>
                    <Text style={[{ color: C(pressHoverColor('System/Light/Blue')) }, font.Body]}>
                      {i18n.t(connectorAlreadyExist && !isEditMode ? 'Edit' : 'Cancel')}
                    </Text>
                  </View>
                )}
              </TouchableElement>
            </View>
          )}
          {isError && (
            <View style={{ position: 'absolute', zIndex: 1000, right: 0 }}>
              <TouchableElement onPress={() => goBack()}>
                {({ boxStyle }) => (
                  <View style={[boxStyle, { padding: scale(15) }]}>
                    <Text
                      style={[
                        {
                          color: C('Link/Light/Primary'),
                        },
                        font.Headline,
                      ]}
                    >
                      {i18n.t('Close')}
                    </Text>
                  </View>
                )}
              </TouchableElement>
            </View>
          )}
          {loaded && !isError && (
            <View style={{ position: 'absolute', zIndex: 1000, right: 0 }}>
              <TouchableElement
                onPress={() => {
                  if (data.isEnabled !== dataHistory.isEnabled) {
                    return submit();
                  }

                  if ((connectorAlreadyExist && !isEditMode) || connectionInProgress) {
                    return goBack();
                  }

                  if (loading || !isDataChanged) {
                    return;
                  }

                  submit();
                }}
              >
                {({ boxStyle, pressHoverColor }) => (
                  <View style={[boxStyle, { padding: scale(15), paddingRight: 0 }]}>
                    {loading ? (
                      <SmallLoading />
                    ) : (
                      <Text
                        style={[
                          {
                            color: C(
                              pressHoverColor(isEditMode ? (isDataChanged ? 'Link/Light/Primary' : 'Label/Light/Quinary') : 'Link/Light/Primary')
                            ),
                          },
                          font.Headline,
                        ]}
                      >
                        {i18n.t('Done')}
                      </Text>
                    )}
                  </View>
                )}
              </TouchableElement>
            </View>
          )}
        </View>
      </View>
      <View style={{ width: '100%', position: 'absolute', zIndex: 100, top: scale(15) }}>
        <Text style={[{ color: C('Label/Light/Primary'), width: '100%', textAlign: 'center' }, font.Headline]}>
          {selectedType?.code || i18n.t('Production Connector')}
        </Text>
      </View>
      {loaded && isError && (
        <View style={{ height: scale(126), justifyContent: 'center', alignItems: 'center' }}>
          <Text style={[font.Body, { color: C('Label/Light/Error') }]}>
            {i18n.t('Something didn’t work')}. {i18n.t('Please try again later')}.
          </Text>
        </View>
      )}
      {!isError && (
        <>
          {loaded ? (
            <ScrollView
              style={isPhone ? { flex: 1 } : { maxHeight: maxHeight - scale(58) - scale(notchTop + 12) }}
              contentContainerStyle={{ paddingBottom: scale(20) }}
            >
              <View style={{ paddingHorizontal: isPhone ? 0 : scale(15) }}>
                {/* Render input connections */}
                <View>
                  <View style={{ marginTop: scale(6) }}>
                    <View>
                      <EditableLine
                        {...{
                          item: {
                            value: dataHistory.isEnabled ?? true,
                            key: 'isEnabled',
                            name: 'Connection',
                            noArrow: true,
                            isLable: true,
                            oneLine: true,
                            small: false,
                            width: 170,
                            type: 'boolean',
                            onChange: (v: string) => updateDataHistory('isEnabled', v),
                          },
                          bg: modalFieldBg,
                          index: 0,
                          array: [{}],
                          C,
                          topBorder: false,
                          bottomBorder: false,
                          roundTop: true,
                          roundBottom: true,
                          editable: true,
                        }}
                      />
                    </View>
                  </View>
                </View>
                <View>
                  <View style={{ marginTop: scale(20) }}>
                    {inputsConnectionTitle && (
                      <View
                        style={{
                          paddingLeft: scale(15),
                          marginTop: scale(8),
                          marginBottom: scale(5),
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexDirection: 'row',
                        }}
                      >
                        <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t(inputsConnectionTitle)}</Text>
                      </View>
                    )}
                    {inputsConnection.map((item, index) => (
                      <EditableLine
                        {...{
                          item: {
                            value: dataHistory[item.key] || '',
                            onChange: (v: string) => updateDataHistory(item.key, v),
                            small: false,
                            ...item,
                          },
                          bg: modalFieldBg,
                          index,
                          array: inputsConnection,
                          C,
                          bottomBorderBox: isPhone,
                          topBorderBox: isPhone,
                          topBorder: index !== 0,
                          roundTop: !isPhone && index === 0,
                          roundBottom: !isPhone && index === inputsConnection.length - 1,
                          editable: isEditMode,
                        }}
                        key={item.key}
                      />
                    ))}
                    {inputsConnectionDescription && (
                      <View style={{ paddingHorizontal: scale(15), marginTop: scale(3) }}>
                        <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>{i18n.t(inputsConnectionDescription)}</Text>
                      </View>
                    )}
                  </View>
                </View>

                {shouldDisplayAPIKey && (
                  <View>
                    <View
                      style={{
                        paddingLeft: scale(15),
                        marginTop: scale(28),
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexDirection: 'row',
                      }}
                    >
                      <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t('API KEY')}</Text>
                    </View>
                    <View style={{ marginTop: scale(5) }}>
                      <EditableLine
                        {...{
                          item: {
                            value: dataHistory?.apiKey,
                            onChange: (v: string) => updateDataHistory('apiKey', v),
                            small: false,
                            key: 'apiKey',
                            name: '',
                            placeholder: hints['apiKey'] || 'API',
                            noArrow: true,
                            isLable: false,
                          },
                          bg: modalFieldBg,
                          index: 0,
                          array: [{}],
                          C,
                          bottomBorderBox: isPhone,
                          topBorderBox: isPhone,
                          topBorder: false,
                          roundTop: true,
                          roundBottom: true,
                          editable: isEditMode,
                        }}
                        key="apiKey"
                      />
                    </View>
                  </View>
                )}

                {/* API Credentials for ProMexS ERP system */}
                {shouldDisplayAPICredentials && (
                  <View>
                    <View
                      style={{
                        paddingLeft: scale(15),
                        marginTop: scale(28),
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexDirection: 'row',
                      }}
                    >
                      <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t('SMART FACTORY API CREDENTIALS')}</Text>
                    </View>
                    <View style={{ marginTop: scale(5) }}>
                      <Line
                        {...{
                          item: {
                            name: 'Send to proMExS',
                            key: 'sendToProMExS',
                            value: '',
                            noArrow: true,
                            onClick: onSendingAPICredentials,
                          },
                          bg: modalFieldBg,
                          index: 0,
                          array: [{}],
                          C,
                          bottomBorderBox: false,
                          topBorderBox: false,
                          topBorder: false,
                          roundTop: true,
                          roundBottom: true,
                          textColor: sendingToProMExS ? 'Label/Light/Quinary' : 'Link/Light/Primary',
                          cursor: 'pointer',
                        }}
                      />
                    </View>
                  </View>
                )}

                {/* Render Database Credentials */}
                <View>
                  <View
                    style={{
                      paddingLeft: scale(15),
                      marginTop: scale(28),
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexDirection: 'row',
                    }}
                  >
                    <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>
                      {i18n.t(`${isMKG_API ? 'USER' : 'DATABASE'} CREDENTIALS`)}
                    </Text>
                  </View>
                  <View style={{ marginTop: scale(5) }}>
                    {inputsDatabase.map((item, index) => (
                      <EditableLine
                        {...{
                          item: {
                            value: dataHistory[item.key] || '',
                            onChange: (v: string) => updateDataHistory(item.key, v),
                            small: false,
                            ...item,
                          },
                          bg: modalFieldBg,
                          index,
                          array: inputsDatabase,
                          C,
                          bottomBorderBox: isPhone,
                          topBorderBox: isPhone,
                          topBorder: index !== 0,
                          roundTop: !isPhone && index === 0,
                          roundBottom: !isPhone && index === inputsDatabase.length - 1,
                          editable: isEditMode,
                        }}
                        key={item.key}
                      />
                    ))}
                  </View>
                </View>
                {/* Time zone */}
                <View>
                  <View
                    style={{
                      paddingLeft: scale(15),
                      marginTop: scale(28),
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexDirection: 'row',
                    }}
                  >
                    <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t('ERP TIME ZONE')}</Text>
                  </View>
                  <View style={{ marginTop: scale(5) }}>
                    <Line
                      {...{
                        item: {
                          name: dataHistory.timeZoneWindows,
                          key: 'timeZoneWindows',
                          value: '',
                          noArrow: !isEditMode,
                          onClick: () => {
                            if (!isEditMode) {
                              return;
                            }
                            setMode(MODES.TIME_ZONE);
                          },
                        },
                        bg: modalFieldBg,
                        index: 0,
                        array: [{}],
                        C,
                        bottomBorderBox: false,
                        topBorderBox: false,
                        topBorder: false,
                        roundTop: true,
                        roundBottom: true,
                        alwaysOneLine: true,
                        onlyLabel: true,
                      }}
                    />
                  </View>
                </View>
                {/* Render filters if available */}
                {Boolean(selectedType?.requiredFilterProperties?.length) && (
                  <View>
                    <View style={{ paddingHorizontal: scale(15), marginTop: scale(28) }}>
                      <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t('FILTERS')}</Text>
                    </View>
                    <View style={{ marginTop: scale(5) }}>
                      {selectedType?.requiredFilterProperties?.map((item, index) => (
                        <EditableLine
                          {...{
                            item: {
                              value: dataHistory[item.code] || '',
                              onChange: (v: string) => updateDataHistory(item.code, v),
                              small: false,
                              placeholder: item.name,
                              ...item,
                            },
                            bg: modalFieldBg,
                            index,
                            array: selectedType?.requiredFilterProperties,
                            C,
                            bottomBorderBox: isPhone,
                            topBorderBox: isPhone,
                            topBorder: index !== 0,
                            roundTop: !isPhone && index === 0,
                            roundBottom: !isPhone && index === selectedType?.requiredFilterProperties.length - 1,
                            editable: isEditMode,
                          }}
                          key={item.code}
                        />
                      ))}
                    </View>
                  </View>
                )}
                {/* Render File Credentials */}
                <View>
                  <View
                    style={{
                      paddingLeft: scale(15),
                      marginTop: scale(28),
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexDirection: 'row',
                    }}
                  >
                    <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t('FILE ACCESS CREDENTIALS')}</Text>
                  </View>
                  <View style={{ marginTop: scale(5) }}>
                    {inputsFile.map((item, index) => (
                      <EditableLine
                        {...{
                          item: {
                            value: dataHistory[item.key] || '',
                            onChange: (v: string) => updateDataHistory(item.key, v),
                            small: false,
                            ...item,
                          },
                          bg: modalFieldBg,
                          index,
                          array: inputsFile,
                          C,
                          bottomBorderBox: isPhone,
                          topBorderBox: isPhone,
                          topBorder: index !== 0,
                          roundTop: !isPhone && index === 0,
                          roundBottom: !isPhone && index === inputsFile.length - 1,
                          editable: isEditMode,
                        }}
                        key={item.key}
                      />
                    ))}
                  </View>
                  <View style={{ paddingHorizontal: scale(15), marginTop: scale(3) }}>
                    <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>
                      {i18n.t('Will be used to access files that are linked in your ERP Software.')}
                    </Text>
                  </View>
                </View>
                {/* Render folders */}
                <View>
                  <View style={{ paddingHorizontal: scale(15), marginTop: scale(28) }}>
                    <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t('MAPPED DRIVES & VIRTUAL FOLDERS')}</Text>
                  </View>
                  <View style={{ marginTop: scale(5) }}>
                    {folders.map((item, index) => (
                      <Line
                        {...{
                          item: {
                            name: insertPathBreakOpportunities(item.folder),
                            key: item.id,
                            value: insertPathBreakOpportunities(item.UNC),
                            noArrow: !isEditMode,
                            onClick: () => {
                              if (!isEditMode) {
                                return;
                              }
                              updateDataHistoryObj({
                                changes: {
                                  editFolder: true,
                                  selectedFolder: item,
                                },
                                dataIsChanged: true,
                              });
                              setMode(MODES.DRIVERS_FOLDERS);
                            },
                          },
                          bg: modalFieldBg,
                          index,
                          array: folders,
                          C,
                          bottomBorderBox: false,
                          topBorderBox: false,
                          topBorder: index !== 0,
                          roundTop: index === 0,
                          roundBottom: !isEditMode && index == folders.length - 1,
                        }}
                        key={item.id}
                      />
                    ))}
                    {isEditMode && (
                      <TouchableElement
                        {...{
                          hitSlop: { top: 0, bottom: 0, left: 0, right: 0 },
                          onPress: () => {
                            updateDataHistoryObj({
                              changes: {
                                editFolder: false,
                                selectedFolder: {},
                              },
                              dataIsChanged: true,
                            });
                            setMode(MODES.DRIVERS_FOLDERS);
                          },
                        }}
                      >
                        {({ pressHoverColor, boxStyle }) => (
                          <View
                            style={[
                              boxStyle,
                              {
                                paddingLeft: scale(15),
                                justifyContent: 'center',
                                borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
                                borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
                                backgroundColor: C(pressHoverColor('Background/Light/Field')),
                                height: scale(42),
                              },
                              folders?.length
                                ? {
                                    borderTopWidth: StyleSheet.hairlineWidth,
                                    borderTopColor: C('Hairline/Light/No trans'),
                                  }
                                : {
                                    borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
                                    borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
                                  },
                            ]}
                          >
                            <Text style={[font.Body, { color: C('Link/Light/Primary') }]}>{i18n.t('Add')}</Text>
                          </View>
                        )}
                      </TouchableElement>
                    )}
                  </View>
                  <View style={{ paddingHorizontal: scale(15), marginTop: scale(3) }}>
                    <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>
                      {i18n.t(
                        'The Smart Factory App shows files linked in your ERP Software. If these file links include mapped drives or virtual folders, simply add the UNC (Universal Naming Convention) paths for those here. This ensures that Smart Factory can seamlessly display your files.'
                      )}
                      {'\n\n'}
                      <Text>
                        <Text style={{ fontWeight: '700' }}>{i18n.t('Mapped Drive')}</Text>:{' '}
                        {i18n.t(
                          'Smart Factory cannot see the network location (UNC path) of the mapped drive automatically. Therefore you need to enter it here.'
                        )}
                      </Text>
                      {'\n\n'}
                      <Text>
                        <Text style={{ fontWeight: '700' }}>{i18n.t('Virtual Folder')}</Text>:{' '}
                        {i18n.t(
                          'If a file cannot be accessed anymore in the old location (e.g. because of a modified folder name), Smart Factory will also search for this file in the virtual folder. A Virtual Folder is also called a Symbolic Link.'
                        )}
                      </Text>
                    </Text>
                  </View>
                </View>
              </View>
            </ScrollView>
          ) : (
            <View style={{ height: scale(126), justifyContent: 'center', alignItems: 'center' }}>
              <SmallLoading />
            </View>
          )}
        </>
      )}
    </View>
  );
};

export default ProductionConnectorSetup;
